import {
  interfaceProjectAsyncAtomFamily,
  interfacePageAsyncAtomFamily,
} from "@zapier/canvas-state";
import { useAtomValue } from "jotai";
import { useState, useEffect } from "react";
import { ModalContainer } from "./ModalContainer";

export function AssetDetailModal({
  selectedAssetId,
  setSelectedAssetId,
  assetType,
  setAssetType,
  pageId,
  nodeId,
}: {
  selectedAssetId: string;
  setSelectedAssetId: (id: string | null) => void;
  assetType: "zap" | "table" | "interface" | null;
  setAssetType: (type: "zap" | "table" | "interface" | null) => void;
  pageId: string | null;
  nodeId: string;
}) {
  const [isMaximized, setIsMaximized] = useState(false);
  const url = getUrl(assetType, selectedAssetId);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelectedAssetId(null);
        setAssetType(null);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [setSelectedAssetId, setAssetType]);

  const handleClose = () => {
    setSelectedAssetId(null);
    setAssetType(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (assetType === "interface") {
    return (
      <InterfaceDetailModal
        selectedAssetId={selectedAssetId}
        setSelectedAssetId={setSelectedAssetId}
        assetType={assetType}
        pageId={pageId}
        nodeId={nodeId}
        isMaximized={isMaximized}
        setIsMaximized={setIsMaximized}
        setAssetType={setAssetType}
      />
    );
  }

  return (
    <ModalContainer
      isMaximized={isMaximized}
      setIsMaximized={setIsMaximized}
      onClose={handleClose}
      onOverlayClick={handleOverlayClick}
      title={`Asset Detail Modal: ${assetType} - ${selectedAssetId}`}
      url={url}
    />
  );
}

function getUrl(assetType: "zap" | "table" | "interface" | null, id: string) {
  if (assetType === "zap") {
    return `https://zapier.com/webintent/edit-zap/${id}`;
  }
  if (assetType === "table") {
    return `https://tables.zapier.com/app/tables/t/${id}`;
  }
  return;
}

function InterfaceDetailModal({
  selectedAssetId,
  setSelectedAssetId,
  assetType,
  setAssetType,
  pageId,
  nodeId,
  isMaximized,
  setIsMaximized,
}: {
  selectedAssetId: string;
  setSelectedAssetId: (id: string | null) => void;
  assetType: "interface";
  pageId: string | null;
  nodeId: string;
  isMaximized: boolean;
  setIsMaximized: (isMaximized: boolean) => void;
  setAssetType: (type: "zap" | "table" | "interface" | null) => void;
}) {
  const interfaceProject = useAtomValue(
    interfaceProjectAsyncAtomFamily({
      interfaceProjectId: selectedAssetId,
      nodeId,
    }),
  );
  const page = useAtomValue(
    interfacePageAsyncAtomFamily({
      interfaceProjectId: selectedAssetId,
      interfacePageId: pageId ?? "",
      nodeId,
    }),
  );
  const projectSlug = interfaceProject?.slug;
  const pageSlug = page?.slug;
  const customDomain = interfaceProject?.customDomain;

  const url = customDomain
    ? `https://${customDomain}/${pageSlug}`
    : `https://${projectSlug}.zapier.app/${pageSlug}`;

  const handleClose = () => {
    setSelectedAssetId(null);
    setAssetType(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <ModalContainer
      isMaximized={isMaximized}
      setIsMaximized={setIsMaximized}
      onClose={handleClose}
      onOverlayClick={handleOverlayClick}
      title={`Asset Detail Modal: ${assetType} - ${selectedAssetId}`}
      url={url}
    />
  );
}
